/**
 * AURA DOM Scanner
 * Watches host DOM for changes and triggers re-application of adjustments
 */

import { Logger } from './logger';

type ScanCallback = () => void;

class DomScanner {
  private observer: MutationObserver | null = null;
  private scanCallbacks: Set<ScanCallback> = new Set();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private debounceMs = 100;
  private isObserving = false;
  private logger = new Logger('DomScanner');
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;

  /**
   * Start observing DOM changes
   */
  start(): void {
    if (this.isObserving) return;

    const target = document.body ?? document.documentElement;
    if (!target) {
      this.logger.warn('No DOM target available for observation');
      return;
    }

    this.observer = new MutationObserver(this.handleMutations.bind(this));

    this.observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-label', 'aria-hidden', 'role'],
      characterData: true,
    });

    this.setupNavigationListeners();
    this.isObserving = true;
    this.logger.info('DOM scanner started');
  }

  /**
   * Stop observing DOM changes
   */
  stop(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.removeNavigationListeners();
    this.isObserving = false;
    this.logger.info('DOM scanner stopped');
  }

  /**
   * Register a callback to be called on DOM changes
   */
  onScan(callback: ScanCallback): () => void {
    this.scanCallbacks.add(callback);
    return () => this.scanCallbacks.delete(callback);
  }

  /**
   * Manually trigger a scan
   */
  triggerScan(): void {
    this.executeScan();
  }

  /**
   * Handle mutation observations
   */
  private handleMutations(mutations: MutationRecord[]): void {
    // Filter out mutations caused by our own changes
    const relevantMutations = mutations.filter((mutation) => {
      const target = mutation.target as HTMLElement;
      return !target.closest?.('[data-aura]');
    });

    if (relevantMutations.length === 0) return;

    // Debounce the scan
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.executeScan();
    }, this.debounceMs);
  }

  /**
   * Execute all scan callbacks
   */
  private executeScan(): void {
    this.logger.debug('Executing scan callbacks', {
      callbackCount: this.scanCallbacks.size,
    });

    this.scanCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        this.logger.error('Error in scan callback', error);
      }
    });
  }

  /**
   * Set up navigation event listeners for SPA support
   */
  private setupNavigationListeners(): void {
    window.addEventListener('popstate', this.handleNavigation);
    window.addEventListener('hashchange', this.handleNavigation);

    // Patch History API for SPA navigation detection
    this.patchHistoryApi();
  }

  /**
   * Remove navigation event listeners and restore patched History API
   */
  private removeNavigationListeners(): void {
    window.removeEventListener('popstate', this.handleNavigation);
    window.removeEventListener('hashchange', this.handleNavigation);
    this.restoreHistoryApi();
  }

  /**
   * Handle navigation events
   */
  private handleNavigation = (): void => {
    this.logger.debug('Navigation detected');
    // Delay scan to allow SPA to render new content
    setTimeout(() => {
      this.executeScan();
    }, 200);
  };

  /**
   * Patch History API to detect SPA navigation.
   * Critical: errors inside the patched functions must NEVER propagate
   * to the host SPA router -- only our own navigation handler is guarded;
   * the original pushState/replaceState is always called first.
   */
  private patchHistoryApi(): void {
    try {
      this.originalPushState = history.pushState;
      this.originalReplaceState = history.replaceState;
      const boundPush = this.originalPushState.bind(history);
      const boundReplace = this.originalReplaceState.bind(history);
      const nav = () => {
        try { this.handleNavigation(); } catch (_) { /* swallow */ }
      };

      history.pushState = (...args) => {
        boundPush(...args);
        nav();
      };

      history.replaceState = (...args) => {
        boundReplace(...args);
        nav();
      };
    } catch (error) {
      this.logger.warn('Could not patch History API', {
        error: String(error),
      });
    }
  }

  /**
   * Restore original History API methods
   */
  private restoreHistoryApi(): void {
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }
  }

  /**
   * Set debounce delay
   */
  setDebounceMs(ms: number): void {
    this.debounceMs = ms;
  }
}

// Singleton instance
export const domScanner = new DomScanner();
